/**
 * The Purkinje / mesopic shift (2026-09-27, Atlas sky/post P2 — realism-additions §1.6).
 *
 * As the eye's adaptation luminance falls from photopic (~3 cd/m²) toward scotopic (~0.01 cd/m²) the rods take over:
 * colour fades and what is left reads blue-grey — the day-for-night look (Kellnhofer, Ritschel, Myszkowski & Seidel
 * 2014, "Modeling luminance perception at absolute threshold"; the Purkinje effect). A light-adapted camera shows none
 * of it, so a physically lit night renders as a dim DAY picture unless the post chain adds it.
 *
 *   rgb' = mix(rgb, Y · tint / Y(tint), k · w(Y)),   Y = Rec.709 luminance of the LINEAR scene colour,
 *
 * k (0..1) — the ADAPTATION (the CPU's: from the metered scene luminance, 0 above 3 cd/m², 1 below 0.01, smooth in
 * log); w(Y) = 1 − smoothstep(2·key, 32·key, Y) — pixels well over the key's level (a lamp, a fire, the moon's
 * disc) are seen by cones and keep their colour; `key` is the scene-linear level of an 18 % card under the frame's
 * light (the CPU's). The tint is (0.6, 0.75, 1.0) normalised to unit luminance, so the shift moves HUE and SATURATION,
 * never brightness (the exposure curve owns brightness).
 *
 * Applied in the linear domain immediately BEFORE the display transform (so every AA / upscale path meets it, and
 * the display-referred grade + LUT come after it). k = 0 is an exact identity (noon is untouched).
 */
import * as THREE from 'three/webgpu';
import { Fn, float, vec3, vec4, uniform, mix, smoothstep, dot } from 'three/tsl';

export interface PurkinjeUniforms {
  /** The adaptation k (0 photopic … 1 scotopic). */
  strength: { value: number };
  /** The scene-linear level of an 18 % card under the frame's light (the per-pixel cone mask's pivot). */
  keyLevel: { value: number };
  /** The rod tint (linear RGB; normalised to unit luminance in the shader). */
  tint: { value: THREE.Vector3 };
}

/** The rod tint of the day-for-night look (a blue-grey; the shader normalises it to unit luminance). */
export const PURKINJE_TINT: [number, number, number] = [0.6, 0.75, 1.0];

export function createPurkinjeUniforms(): PurkinjeUniforms {
  return {
    strength: uniform(0) as unknown as { value: number },
    keyLevel: uniform(0.18) as unknown as { value: number },
    tint: uniform(new THREE.Vector3(...PURKINJE_TINT)) as unknown as { value: THREE.Vector3 },
  };
}

/** The largest finite fp16 — the shift's working clamp (THE NaN GUARD in `applyPurkinje`). */
export const PURKINJE_FP16_MAX = 65504;

/**
 * CPU mirror of `applyPurkinje` for one linear RGB pixel (tests; the same guard): k = strength × the cone mask; the
 * shifted colour only where k > 0, else the input verbatim (Inf stays Inf — never NaN).
 */
export function applyPurkinjeCpu(rgb: [number, number, number], strength: number, keyLevel: number, tint: [number, number, number] = PURKINJE_TINT): [number, number, number] {
  const L = [0.2126, 0.7152, 0.0722];
  const c = rgb.map((v) => Math.min(v, PURKINJE_FP16_MAX));
  const Y = c[0] * L[0] + c[1] * L[1] + c[2] * L[2];
  const ty = Math.max(1e-4, tint[0] * L[0] + tint[1] * L[1] + tint[2] * L[2]);
  const k2 = Math.max(keyLevel, 1e-8);
  const x = Math.min(1, Math.max(0, (Y - 2 * k2) / (30 * k2)));
  const cones = 1 - x * x * (3 - 2 * x);
  const k = Math.min(1, Math.max(0, strength)) * cones;
  if (!(k > 0)) return [rgb[0], rgb[1], rgb[2]];
  return [0, 1, 2].map((i) => c[i] * (1 - k) + (tint[i] / ty) * Y * k) as [number, number, number];
}

/** Wrap a linear HDR colour node with the shift (the pipeline applies it inside its display boundary). */
export function applyPurkinje(input: unknown, u: PurkinjeUniforms): unknown {
  const strength = u.strength as any, keyLevel = u.keyLevel as any, tint = u.tint as any;
  return Fn(() => {
    const LUMA = vec3(0.2126, 0.7152, 0.0722); // Rec.709 (built inside the Fn: no node construction at import)
    const c = vec4(input as never);
    // THE NaN GUARD (2026-09-27): the stage must never MANUFACTURE a NaN. An Inf HDR pixel (a sun disc past fp16) made
    // mix(c, t·Y, 0) = c + (t·Y − c)·0 = Inf·0 = NaN at noon (k = 0 is supposed to be an identity), and at k = 1
    // mix = c·(1 − k) + … = Inf·0 again — the bloom then spreads the NaN. The mix runs on the fp16-clamped colour and
    // its result is SELECTED (never blended) only where k > 0, so k = 0 — or a NaN k (a NaN compare is false) — passes
    // the input through untouched.
    const rgb = c.rgb.min(vec3(PURKINJE_FP16_MAX, PURKINJE_FP16_MAX, PURKINJE_FP16_MAX));
    const Y = dot(rgb, LUMA);
    const t = tint.div(dot(tint, LUMA).max(1e-4));
    const k2 = keyLevel.max(1e-8);
    const cones = float(1).sub(smoothstep(k2.mul(2), k2.mul(32), Y));
    const k = strength.clamp(0, 1).mul(cones);
    return vec4(k.greaterThan(0).select(mix(rgb, t.mul(Y), k), c.rgb), c.a);
  })();
}

/**
 * The adaptation k for an adaptation luminance `nits` (cd/m²): 0 at ≥ 3 (photopic), 1 at ≤ 0.01 (scotopic),
 * smoothstep in log₁₀ between (the mesopic range). Pure — the CPU drives the uniform with it.
 */
export function purkinjeStrength(nits: number, photopic = 3, scotopic = 0.01): number {
  if (!(nits > 0)) return 1;
  const a = Math.log10(photopic), b = Math.log10(scotopic);
  const t = Math.min(1, Math.max(0, (a - Math.log10(nits)) / (a - b)));
  return t * t * (3 - 2 * t);
}
